import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSelect, useSwitch } from '@chilecompra/react-kit'
import { useTheme } from 'styled-components'
import { Card, DataTable, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { generateYears } from '../../modules/utils/years'
import HorizontalBarChart from '../../components/Charts/HorizontalBarChart'
import { onGetAgilePurchaseTradedProvidersThunk } from './AgilePurchaseTradedProviders.actions'
import { Content, GridContainer, GridItem, Description, Link, Root, Title } from './AgilePurchaseTradedProviders.styles'
import {
  INDICATORS_OPTIONS,
  INDICATORS_DICTIONARY,
  PROVIDERS_INDICATORS_TRANSLATION_DICTIONARY,
  REGIONS_ALL
} from '../../config/settings/constants'
import InfoAgilePurchaseTradedProviders from '../../components/Infographics/InfoAgilePurchaseTradedProviders'
import routes from '../../config/settings/routes'
import { SORT_DIRECTIONS_DICTIONARY } from '@chilecompra/react-kit/components/Tables/DataTable.utils'

/**
 * The AgilePurchaseTradedProvider's container.
 */
const AgilePurchaseTradedProviders = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { endYear, latestUpdate, loading, regions: regionOptions } = useSelector(state => state.topNavbar)
  const { data, csv } = useSelector(state => state.agilePurchaseTradedProviders)
  const yearsOptions = useMemo(() => generateYears(endYear), [endYear])
  const [activeView, setActiveView] = useState(0)

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => {
      dispatch(onGetAgilePurchaseTradedProvidersThunk({ year: updatedValue, region: regionValues }))
    },
    overwriteCallback: () => endYear
  })

  const { value: regionValues, onChange: handleChangeRegion } = useSelect({
    initialValue: REGIONS_ALL,
    changeCallback: updatedRegion => {
      dispatch(onGetAgilePurchaseTradedProvidersThunk({ year: yearValue, region: updatedRegion }))
    }
  })

  const { value: indicatorValue, onChange: handleChangeIndicator } = useSelect({
    initialValue: INDICATORS_DICTIONARY.amount
  })

  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()

  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('agilepurchase_top_providers', `CompraAgil_PrincipalesProveedores_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `CompraAgil_PrincipalesProveedores_${yearValue}`)
  })
  const handleViewChange = newView => {
    setActiveView(newView)
    handleChangeView(newView)
  }
  const handleDataLabelFormatter = useCallback(
    function () {
      if (indicatorValue === INDICATORS_DICTIONARY.amount) {
        return currencyValue
          ? `<strong style="background-color: transparent;">USD$${formatAmount(this.y)}</strong>`
          : `<strong style="background-color: transparent;">$${formatAmount(this.y)}</strong>`
      }

      if (indicatorValue === INDICATORS_DICTIONARY.orders) {
        return `<strong>${this.y}</strong>`
      }

      return `<strong>${this.y}</strong>`
    },
    [indicatorValue, currencyValue]
  )
  const handleLabelFormatter = useCallback(function () {
    const [records] = this.chart.series
    const row = records.userOptions.data[this.pos]
    return `
      <a href="${routes.provider.replace(
        /:dni/,
        row.providerRUT
      )}" style="color: ${theme.palette.brand.primary}; text-decoration: underline; text-transform: uppercase;">${this.value}</a>
      <br />
      <span style="color: ${theme.palette.brand.black3}">${row.providerRUT}</span>
    `
  }, [])

  const getEnhancedData = useCallback((collection = [], rows) => {
    const sortField = indicatorValue === INDICATORS_DICTIONARY.amount ? 'amount' : 'orders'

    const sortedData = [...collection].sort((a, b) => b[sortField] - a[sortField]).slice(0, rows)

    return sortedData.map(
      datum => {
        const updatedDatum = { ...datum, amount: currencyValue ? datum.amountUSD : datum.amount }
        return {
          ...datum,
          color: theme.palette.brand.primary,
          y: updatedDatum[PROVIDERS_INDICATORS_TRANSLATION_DICTIONARY[indicatorValue]]
        }
      },
      [indicatorValue]
    )
  })
  const getCategories = useCallback((collection = [], rows) => {
    const sortField = indicatorValue === INDICATORS_DICTIONARY.amount ? 'amount' : 'orders'
    return [...collection]
      .sort((a, b) => b[sortField] - a[sortField])
      .slice(0, rows)
      .map(datum => datum.providerName, [indicatorValue])
  })

  useEffect(() => {
    if (!endYear) return
    dispatch(onGetAgilePurchaseTradedProvidersThunk({ year: endYear, region: regionValues }))
  }, [endYear])

  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Principales proveedores nacional y regional
        </Typography>
        <GridContainer>
          <GridItem xs={6}>
            <Title>Proveedores que más transan a través de Compra Ágil</Title>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={5}>
            <Description>
              Revisa los montos y la cantidad de órdenes de compra realizadas mediante Compra Ágil de los principales
              proveedores del Estado. Puedes revisar la información por región, año e indicador.
            </Description>
          </GridItem>
        </GridContainer>

        <DataLayout
          disableCaptureImage={disableCaptureImage}
          disableDownloadFile={disableDownloadFile}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              <GridItem md={10} xs={12}>
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Año"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem md={4} xs={12}>
                    <Select
                      label="Región"
                      loading={loading}
                      options={regionOptions}
                      value={regionValues}
                      onChange={handleChangeRegion}
                    />
                  </GridItem>
                  {activeView === DataLayoutViews.CHART && (
                    <GridItem md={4} xs={12}>
                      <Select
                        label="Indicador"
                        loading={loading}
                        options={INDICATORS_OPTIONS}
                        value={indicatorValue}
                        onChange={handleChangeIndicator}
                      />
                    </GridItem>
                  )}
                </GridContainer>
              </GridItem>

              <GridItem md={2} xs={12}>
                <Switch
                  checked={currencyValue}
                  color="primary"
                  labelLeft="CLP"
                  labelRight="USD"
                  onChange={handleChangeCurrency}
                  margin="0 0 0 32px"
                />
              </GridItem>
            </GridContainer>
          }
          infographic={<InfoAgilePurchaseTradedProviders />}
          noData={!loading && data?.length === 0}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleViewChange}
          onDownloadFile={handleDownloadFile}
        >
          <DataLayoutChild view={DataLayoutViews.CHART}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer>
                <GridItem xs={12}>
                  <HorizontalBarChart
                    key={`chart-${currencyValue}-${indicatorValue}`}
                    categories={getCategories(data, 10)}
                    backgroundColor={theme.palette.custom.transparentWhite}
                    data={getEnhancedData(data, 10)}
                    dataLabelFormatter={handleDataLabelFormatter}
                    labelFormatter={handleLabelFormatter}
                    id="agilepurchase_top_providers"
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
          <DataLayoutChild view={DataLayoutViews.TABLE}>
            <Card color="blank" elevation="1">
              <GridContainer padding="0 0 20px">
                <GridItem xs={12}>
                  <DataTable
                    headers={[
                      { label: 'Nombre Proveedor', value: 'providerName' },
                      { label: 'RUT proveedor', value: 'providerRUT' },
                      { label: 'Monto transado', value: 'amount' },
                      { label: 'N° de órdenes de compra', value: 'orders' }
                    ]}
                    headersCellsProps={[
                      { enableSort: true, width: '50%' },
                      { enableSort: true, width: '15%' },
                      {
                        enableSort: true,
                        initialDirection: SORT_DIRECTIONS_DICTIONARY.DESC,
                        initialSort: true,
                        width: '15%'
                      },
                      { enableSort: true, width: '20%' }
                    ]}
                    initialRows={15}
                    loading={loading}
                    records={getEnhancedData(data, 15)}
                    recordsDecorators={[
                      ({ providerRUT, providerName }) => (
                        <Link style={{ textDecoration: 'underline' }} to={routes.provider.replace(/:dni/, providerRUT)}>
                          {providerName}
                        </Link>
                      ),
                      ({ providerRUT }) => providerRUT,
                      ({ amount, amountUSD }) =>
                        currencyValue ? `USD ${formatAmount(amountUSD)}` : `$${formatAmount(amount)}`,
                      ({ orders }) => orders
                    ]}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
        </DataLayout>
      </Content>
    </Root>
  )
}

export default AgilePurchaseTradedProviders
